<template>
	<section class="products" :style="`--no-image-text:  '${$t('shoplist.noImage')}';`" v-htmlclass="customHtmlClasses">
		<ShopMenuHeader
			v-if="menuMode"
			class="menu-header--mobile insicht insicht--fade-up insicht--fast"
			:title="menuLocationName"
			:subtitle="$t('shoplist.menu.title')"
		/>
		<div class="products__wrapper">
			<div class="products__categories insicht insicht--fade-up insicht--fast" ref="categories">
				<ListScrollPane
					class="products__categories-wrapper"
					:class="{
						'products__categories-wrapper--main': showSubCategories
					}"
					v-slot="{canScroll, scrolledTop, scrolledBottom, scrolled}"
					:to-watch="categories"
				>
					<ShoplistFilter
						:filters="filters"
						:filtersActive="filtersActive"
						@apply="onFilterApply"
					/>

					<template>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--before"
							data-scrollcontainer-up
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledTop,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
						<div
							class="products__categories-group"
							ref="categoryGroup"
							data-scrollcontainer
						>
							<ul class="products__categories-list">
								<!-- TODO: this should be the default category for a user that is logged in. -->
								<!-- FIXME: when this category is active and the page is reloaded, the wrong category is highlighted
								and you can't click on this one, because the hash is still set to favorite. -->

								<ShoplistCategory
									v-if="showPersonalStartpage"
									ref="categoryLink"
									:category="{
										svgIconKey: 'category-favorites',
										name: $t('shoplist.favorites.title')
									}"
									:active="activeCategory == favoriteCode"
									data-scrollcontainer-item
									@click="onFavoriteProducts"
								/>

								<!-- loop -->
								<ShoplistCategory
									ref="categoryLink"
									v-for="category in categories"
									:key="category.code"
									:category="category"
									:active="currentScrollCategory && !showSubCategories ? currentScrollCategory == category.code : activeCategory == category.code"
									data-scrollcontainer-item
									@click="onChangeCategory(category)"
								/>
								<!-- loop end -->
							</ul>
						</div>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--after"
							data-scrollcontainer-down
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledBottom,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
					</template>
				</ListScrollPane>

				<ListScrollPane
					class="products__categories-wrapper products__categories-wrapper--subcategories"
					v-slot="{canScroll, scrolledTop, scrolledBottom, scrolled}"
					:to-watch="currentSubCategories"
					v-if="showSubCategories"
				>
					<template>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--before products__categories-group-overlap--subcategory"
							data-scrollcontainer-up
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledTop,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
						<div
							class="products__categories-group"
							ref="subCategoryGroup"
							data-scrollcontainer
						>
							<ul class="products__categories-list">
								<!-- loop -->
								<ShoplistCategory
									ref="categoryLink"
									v-for="category in currentSubCategories"
									:key="category.code"
									:category="category"
									:active="currentScrollCategory ? currentScrollCategory == category.code : activeSubCategory == category.code"
									modifier="subcategory"
									data-scrollcontainer-item
									@click="onChangeSubCategory(category)"
								/>
								<!-- loop end -->
							</ul>
						</div>
						<div
							class="products__categories-group-overlap products__categories-group-overlap--after products__categories-group-overlap--subcategory"
							data-scrollcontainer-down
							:class="{
								'products__categories-group-overlap--visible': canScroll && !scrolledBottom,
								'products__categories-group-overlap--scrolled': scrolled,
							}"
						/>
					</template>
				</ListScrollPane>

			</div>
			<div
				class="products__inner insicht insicht--fade-in insicht--fast"
				:class="{
					'products__inner--public': !hasBasket && !menuMode,
					'products__inner--menu': menuMode,
				}"
			>

				<ShopHeader v-if="hasBasket && !menuMode"/>

				<ShopMenuHeader
					v-if="menuMode"
					:title="menuLocationName"
					:subtitle="$t('shoplist.menu.title')"
				/>

				<div
					v-if="hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder && groupOrderIsOwner"
					class="group-order-share"
				>
					<div
						class="group-order-share__text"
						v-html="$t('grouporder.share.text')"
					/>
					<div class="group-order-share__link">
						<div
							class="group-order-share__link-text"
							v-html="groupOrderJoinUrl"
						/>
						<div
							v-if="copyGroupOrderUrlCopied"
							class="group-order-share__link-success"
							v-html="$t('grouporder.share.copied')"
						/>
						<button
							class="group-order-share__link-button"
							type="button"
							@click.prevent="onCopyGroupOrderUrl(groupOrderJoinUrl)"
						>
							<SVGDuplicate class="group-order-share__link-button-icon"/>
						</button>
						<button
							class="group-order-share__link-button"
							type="button"
							@click.prevent="onGroupOrderQR"
						>
							<SVGQrCode class="group-order-share__link-button-icon"/>
						</button>
					</div>
				</div>

				<div
					v-if="hasBasket && isLoggedIn && lastOrdersFiltered.length && !showFavorites && !menuMode"
					class="last-orders-teaser"
				>
					<SVGBag class="last-orders-teaser__icon"/>
					<div class="last-orders-teaser__content">
						<div
							class="last-orders-teaser__text"
							v-html="$t('shoplist.lastOrders.teaserText')"
						/>
						<Button
							class="last-orders-teaser__button"
							level="tertiary"
							:text="$t('shoplist.lastOrders.teaserButton')"
							type="button"
							arrow
							@click.prevent="onFavoriteProducts"
						/>
					</div>
				</div>

				<LastOrders
					v-if="hasBasket && isLoggedIn && showFavorites && !menuMode"
					:orders="lastOrdersFiltered"
					class="products__last-orders"
					@click="onLastOrderClick"
				/>

				<h2
					class="products__title"
					v-if="isLoggedIn && favoriteProducts.length && showFavorites && !menuMode"
					v-html="$t('shoplist.favorites.title')"
				/>

				<Loader
					class="products__loader"
					:show="productsLoading && !loading"
				/>

				<div
					class="products__grid"
					:class="{
						'products__grid--public': !hasBasket && !menuMode,
						'products__grid--menu': menuMode,
					}"
					v-if="productsEnhanced && !productsLoading"
				>
					<div
						class="products__grid-empty"
						v-if="!productsEnhanced.length"
						v-html="$t('shoplist.noProducts')"
					/>
					<template v-if="!showFavorites && currentCategoryIsOverview">
						<div
							v-for="category in productsEnhancedCategories"
							:key="category.category.code"
							class="products__category products__grid"
							:class="{
								'products__grid--public': !hasBasket && !menuMode,
								'products__grid--menu': menuMode,
							}"
						>
							<ShoplistCategoryHeader
								ref="categoryHeader"
								:category="category.category"
							/>
							<!-- loop -->
							<ShoplistProduct
								v-for="(product, index) in category.products"
								:key="category.category.code + product.code"
								:product="product"
								:locked="lockedProducts[`${category.category.code}-${product.code}`]"
								:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
								@addToCart="onAddToCart($event, category.category.code)"
								@config="onConfig"
								@click="onClickProduct($event, category.category.code)"
								@orderbox="onOrderboxOpen"
							/>
							<template v-if="category.children && category.children.length">
								<div
									v-for="(child, i) in category.children"
									class="products__category-child products__grid"
								>
									<h3 class="products__category-child-header">
										<component
											v-if="iconComponent(child.category.svgIconKey)"
											:is="iconComponent(child.category.svgIconKey)"
											class="products__category-child-header-icon"
										/>
										<SVGInfo
											v-else
											class="products__category-child-header-icon"
										/>

										<h2
											class="products__category-child-header-title"
											v-html="child.category.name"
										/>
									</h3>
									<ShoplistProduct
										v-for="(product, index) in child.products"
										:key="child.category.code + product.code"
										:product="product"
										:locked="lockedProducts[`${child.category.code}-${product.code}`]"
										:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
										@addToCart="onAddToCart($event, child.category.code)"
										@config="onConfig"
										@click="onClickProduct($event, child.category.code)"
										@orderbox="onOrderboxOpen"
									/>
								</div>
							</template>
							<!-- loop end -->
						</div>
					</template>
					<template v-else>
						<!-- loop -->
						<ShoplistProduct
							v-for="(product, index) in productsEnhancedLimited"
							:key="product.code"
							:product="product"
							:locked="lockedProducts[product.code]"
							:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
							@addToCart="onAddToCart"
							@config="onConfig"
							@click="onClickProduct"
							@orderbox="onOrderboxOpen"
						/>
						<!-- loop end -->
					</template>
				</div>
			</div>
			<div
				class="products__cart-placeholder"
				v-if="hasBasket && !menuMode"
			>
				<OverlaySidebarCart
					class="insicht insicht--fade-up insicht--fast"
					ref="sidebarcart"
					is-inline
					:locked-products="lockedProducts"
					@addToCart="onAddToCart"
					@config="onConfig"
				/>

			</div>
		</div>

		<MobileStickyCart v-if="!menuMode"/>
		<Modal
			overlay-type="product-detail"
			ref="modal"
			@close="onModalProductClose"
		>
			<OverlayProduct
				v-if="modalProductCode"
				:code="modalProductCode"
				:product-data="modalProduct"
				:locked="lockedProducts[modalProductCategory ? `${modalProductCategory}-${modalProductCode}` : modalProductCode]"
				:disabled="cartIsGroupOrderLocked || groupOrderParticipantReady"
				@addToCart="onAddToCart($event, modalProductCategory)"
				@config="onConfig"
				@click="onClickProduct"
				@orderbox="onOrderboxOpen"
				is-shop
			/>
		</Modal>
		<Modal
			v-if="hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder"
			overlay-type="group-order-qr"
			ref="groupOrderQR"
			narrow
		>
			<div class="overlay-dialog">
				<h2
					class="overlay-dialog__title"
					v-html="$t('grouporder.share.qrTitle')"
				/>
				<div
					class="overlay-dialog__text"
					v-html="$t('grouporder.share.qrText')"
				/>
				<div class="group-order-share">
					<div class="group-order-share__link">
						<div
							class="group-order-share__link-text"
							v-html="groupOrderJoinUrl"
						/>
						<div
							v-if="copyGroupOrderUrlCopied"
							class="group-order-share__link-success"
							v-html="$t('grouporder.share.copied')"
						/>
						<button
							class="group-order-share__link-button"
							type="button"
							@click.prevent="onCopyGroupOrderUrl(groupOrderJoinUrl)"
						>
							<SVGDuplicate class="group-order-share__link-button-icon"/>
						</button>
					</div>
				</div>
				<QRCode
					:content="groupOrderJoinUrl"
				/>
			</div>
		</Modal>
		<OverlayError/>
		<OverlayChildProducts
			v-if="hasFeature('FEATURE_CHILDPRODUCTS') && hasBasket && !menuMode"
			ref="childproductsOverlay"
			@edit="onChildProductsEdit"
			@add="onChildProductsAdd"
		/>
	</section>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import urlWatcher from '@/helpers/urlwatcher'
import GenericMixin from '@/mixins/GenericVueMixin'
import OverlaySidebarCart from '@/pages/OverlaySidebarCart'
import OverlayError from '@/pages/OverlayError';
import ShoplistFilter from '@/components/ShoplistFilter'
import ShoplistProduct from '@/components/ShoplistProduct'
import ShoplistCategory from '@/components/ShoplistCategory'
import ShoplistCategoryHeader from '@/components/ShoplistCategoryHeader'
import OverlayProduct from '@/pages/OverlayProduct'
import OverlayChildProducts from '@/pages/OverlayChildProducts'
import MobileStickyCart from '@/components/MobileStickyCart'
import Button from '@/components/Button'
import Modal from '@/components/Modal'
import ListScrollPane from '@/components/ListScrollPane'
import Loader from '@/components/Loader';
import LastOrders from '@/components/LastOrders';
import ShopHeader from '@/components/ShopHeader';
import ShopMenuHeader from '@/components/ShopMenuHeader';
import QRCode from '@/components/QRCode'
import htmlComponent from "htmlcomponent"
import {eventhub, EVENT_CART_TIMECHANGED, EVENT_SHOP_INITIALIZED} from '@/store/eventhub'
import scrrroll from 'scrrroll';
import { supportsPassive } from '@/utils/features'
import { getUrlParams } from '@/utils/urlParams'
import { dayjs } from '@/helpers/dayjs'

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		OverlaySidebarCart,
		OverlayError,
		ShoplistFilter,
		ShoplistProduct,
		ShoplistCategory,
		ShoplistCategoryHeader,
		OverlayProduct,
		OverlayChildProducts,
		MobileStickyCart,
		Button,
		Modal,
		ListScrollPane,
		Loader,
		LastOrders,
		ShopHeader,
		ShopMenuHeader,
		QRCode,
	},
	data() {
		return {
			shopInitialized: false,
			lockedProducts: {},
			showFavorites: false,
			modalProduct: null,
			modalProductCode: null,
			modalProductCategory: null,
			categoryScrollStart: false,
			categoryScrollEnd: false,
			productLoadThresholdMin: 60,
			productLoadThresholdProgression: 5,
			productLoadThresholdMax: 90,
      productsPerPage: 16,
      productPagesLoaded: 1,
			currentScrollCategory: null,
			copyGroupOrderUrlTimeout: null,
			copyGroupOrderUrlCopied: false,

			menuMode: !!this.$root.options.menuMode,
			menuChannel: this.$root.options.menuChannel,
			menuLocationName: this.$root.options.menuLocationName,
			menuTimedAvailability: this.$root.options.menuTimedAvailability,
			reloadTimeout: 0,
		}
	},
	computed: {
		...mapGetters("productlist", [
			"products",
			"categories",
			"hasSubCategories",
			"currentCategorySiblings",
			"currentCategoryIsOverview",
			"currentCategoriesHaveOverview",
			"currentOverviewCategory",
			"currentOverviewCategoryIsTop",
			"filters",
			"filtersActive",
			"currentTaxon",
			"currentTaxonPath",
			"currentSubCategories",
			"favoriteCode",
			"productsLoading",
			"nextProductAvailablityChangeTime",
		]),
		...mapGetters("account", [
			"isLoggedIn",
			"lastOrders",
			"favoriteProducts",
			"accountUserDetail"
		]),
		...mapGetters("cart", [
			"shippingMethod",
			'hasBasket',
			'cartIsGroupOrder',
			'cartIsGroupOrderLocked',
		]),
		...mapGetters("grouporder", [
			'groupOrderParticipantReady',
			'groupOrderIsOwner',
			'groupOrderJoinUrl',
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters([
			'initialized',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		customHtmlClasses() {
			return {
				'shop--public': !this.hasBasket && !this.menuMode,
				'shop--menu': this.menuMode,
			}
		},
		activeCategory() {
			return this.currentTaxon == this.favoriteCode ? this.favoriteCode : this.currentTaxonPath[0] && this.currentTaxonPath[0].code
		},
		activeSubCategory() {
			return this.currentTaxonPath.length > 1 ? this.currentTaxonPath[1].code : ""
		},
		showSubCategories() {
			return this.currentSubCategories.length > 0
		},
		showPersonalStartpage() {
			return !!(this.hasBasket && this.isLoggedIn && (this.favoriteProducts.length || this.lastOrdersFiltered.length) && !this.menuMode);
		},
		productsLimit() {
			if (this.currentCategoryIsOverview) {
				return this.productPagesMax * this.productsPerPage; // TODO: test performance with large category, specially on mobile
			}

			return this.productPagesLoaded * this.productsPerPage;
    },
		productPagesMax() {
			let productsLength = this.productsEnhanced.length;

			if (!this.showFavorites && this.currentCategoryIsOverview) {
				productsLength = this.productsEnhancedCategories.reduce((p, c) => c.products.length + p, 0);
			}

			return Math.ceil(productsLength / this.productsPerPage)
		},
		productLoadThreshold() {
			return Math.min(this.productLoadThresholdMax, this.productLoadThresholdMin + (this.productLoadThresholdProgression * (this.productPagesLoaded - 1)));
		},
		productsEnhanced() {
			let filterIds = this.filtersActive

			//check favorite products
			//TODO: currently done the easy way, but it may have implications if we toss in the normal products
			let products = this.showFavorites ? this.favoriteProducts : this.products

			return products
				.map((p, i) => {
					return {
						...p,
						special: i == 0,
						price: 0,
						selectedVariant: p.variants[0].code,
					}
				})
				.filter(product => product.stock != 0 && product.variants.find(v => v.stock != 0) != null) // filter out products with 0 stock or any with only 0 variants
				.filter(product =>
					filterIds.length > 0
						? filterIds.reduce((p, c) => p && product.tags.includes(c), true)
						: true
				)
		},
		productsEnhancedLimited() {
			return this.productsEnhanced.slice(0, this.productsLimit)
		},
		lastOrdersFiltered() {
			return this.lastOrders
				.filter(order => order.shipmentCode === this.shippingMethod.code)
				.slice(0,3);
		},
		productsEnhancedCategories () {
			let products = this.productsEnhanced
			let siblings = this.currentCategorySiblings

			// return empty tree
			if (!products.length || !siblings.length) {
				return []
			}

			let map = siblings.reduce((p, c) => {
				p[c.code] = p[c.code] || []

				c.children?.forEach(child => {
					p[child.code] = p[child.code] || []
				})

				return p
			}, {})

			products.some((p, i) => {
				// reset special product
				p.special = false;

				// display multi
				// let filter = siblings.filter(s => p.taxons.includes(s.code))
				// 	.forEach(find => map[find.code].push(p))

				Object.keys(map).forEach(cat => {
					if (p.taxons.includes(cat)) {
						map[cat].push(p)
					}
				})

				// let find = siblings.find(s => p.taxons.includes(s.code))
				// if (find) {
				// 	map[find.code].push(p)
				// }
				return false
			})

			return siblings.reduce((p, c) => {
				if (map[c.code]?.length || c.children?.some(cc => map[cc.code]?.length)) {
					let categoryProducts = map[c.code]

					// sort per category
					const currentTaxon = c.code
					categoryProducts.sort((a, b) => (a.taxonsSort[currentTaxon] != null ? a.taxonsSort[currentTaxon] : Number.MAX_VALUE) - (b.taxonsSort[currentTaxon] != null ? b.taxonsSort[currentTaxon] : Number.MAX_VALUE))

					const children = c.children?.reduce((children, child) => {
						if (map[child.code]?.length) {
							let categoryProducts = map[child.code]

							// sort per category
							const currentTaxon = child.code
							categoryProducts.sort((a, b) => (a.taxonsSort[currentTaxon] != null ? a.taxonsSort[currentTaxon] : Number.MAX_VALUE) - (b.taxonsSort[currentTaxon] != null ? b.taxonsSort[currentTaxon] : Number.MAX_VALUE))

							children.push({
								category: child,
								products: categoryProducts
							})

						}
						return children
					}, []) || []

					p.push({
						category: c,
						products: categoryProducts,
						children,
					})
				}
				return p
			}, [])
		},
		productsEnhancedCategoriesLimited () {
			let categories = this.productsEnhancedCategories
			const limit = this.productsLimit

			let count = 0;
			let limitedCategories = categories.reduce((p, {category, products}) => {
				if ((count + 1) <= limit) {
					// limit products
					let newCount = count + products.length
					if (newCount > limit) {
						newCount = limit
						products = products.slice(0, limit - count)
					}
					p.push({
						category,
						products,
					})
					count = newCount
				}
				return p;
			}, []);

			return limitedCategories;
		},
	},
	watch: {
		currentScrollCategory(newValue, oldValue) {
			if (newValue !== oldValue && this.$refs.categoryLink?.length) {
				const activeCategoryLink = [...this.$refs.categoryLink].find(c => c.category.code == newValue);
				if (activeCategoryLink) {
					activeCategoryLink.$parent.scrollTo(activeCategoryLink.$el);
				}
			}
		}
	},
	methods: {
		async onCopyGroupOrderUrl(value) {
			try {
				await navigator.clipboard.writeText(value)
				this.copyGroupOrderUrlCopied = true
				clearTimeout(this.copyGroupOrderUrlTimeout)
				this.copyGroupOrderUrlTimeout = setTimeout(() => {
					this.copyGroupOrderUrlCopied = false
				}, 2000)
			}
			catch (err) {
				this.isDebug() && console.error(err)
			}
		},
		onGroupOrderQR() {
			this.$refs.groupOrderQR?.open()
		},
		onClickProduct(code, categoryCode = null) {
			this.modalProductCategory = categoryCode;
			urlWatcher.change({taxon: this.currentScrollCategory || this.currentTaxon, product: code});
		},
		onModalProductOpen(code) {
			this.modalProduct = this.getProduct(code);
			this.modalProductCode = code;
			if (this.modalProductCode) {
				this.$refs.modal.open();
			}
		},
		onModalProductClose() {
			urlWatcher.change({product: null});
			this.modalProduct = null;
			this.modalProductCode = null;
			this.modalProductCategory = null;
		},
		onOrderboxOpen() {
			htmlComponent.send('overlay:open', {type: 'overlay-orderbox'});
		},
		onOrderboxClose() {
			htmlComponent.send('overlay:close', {type: 'overlay-orderbox'});
		},
		async onConfig(product) {
			htmlComponent.send('overlay:open', {type: 'overlay-childproducts', product, selectedCodes: product.defaultChildProducts});
		},
		async onAddToCart(item, categoryCode = null) {
			const lockKey = categoryCode ? `${categoryCode}-${item.productCode}` : item.productCode;
			Vue.set(this.lockedProducts, lockKey, true);
			try {
				const lastItem = await this.$store.dispatch('cart/addItems', {items: [item]})
				// HACK: we currently inform the sidebarcart about the add. This logic has to be changed in the future
				if (lastItem) {
					// currently we get only an item if its really an add, not just a quantity change
					this.$refs.sidebarcart.onNotifyProductAdded(lastItem)
				}
			}
			catch(err) {
				this.$store.dispatch('dialog/showDialog', {
					title: err?.response?.data?.errors?.availability ? this.$t('shoplist.product.errors.availability') : this.$t('forms.errors.error'),
					message: err?.response?.data?.errors?.availability?.join('<br>') || this.$t('forms.errors.generic'),
					confirm: 'OK',
				});
			}
			Vue.set(this.lockedProducts, lockKey, false);
		},
		onChangeCategory(category) {
			this.showFavorites = false;
			this.productPagesLoaded = 1;
			let toCat = category.code

			// instead switch to first child
			if (
				(
					!this.currentOverviewCategoryIsTop ||
					(
						!this.currentCategoriesHaveOverview &&
						!this.currentCategoryIsOverview
					)
				) &&
				category &&
				category.children &&
				category.children.length > 0
			) {
				toCat = category.children[0].code
			}
			urlWatcher.change({"taxon": toCat, product: null})
		},
		onChangeSubCategory(category) {
			urlWatcher.change({"taxon": category.code, product: null})
		},
		onFavoriteProducts() {
			this.productPagesLoaded = 1;
			urlWatcher.change({"taxon": this.favoriteCode, product: null})
		},
		getProduct(code) {
			return this.productsEnhanced.filter(product => product.code == code)[0];
		},
		async onHashChange(params) {
			if (params.taxon &&
				(
					(this.currentScrollCategory && params.taxon != this.currentScrollCategory) ||
					(!this.currentScrollCategory && params.taxon != this.currentTaxon)
				)
			) {
				// this.loading = true
				if (params.taxon == this.favoriteCode) { //TODO: check what happens with logged in vs non
					window.scrollTo(0, 0);
					this.productPagesLoaded = 1;
					this.showFavorites = true
					//HACK: this commit should not be here, but its marked for future cleanup
					this.$store.commit('productlist/setActiveCategory', { item: this.favoriteCode })
					await this.$store.dispatch('account/loadFavoriteProducts')
						// HACK: favorite Products currently fail on the live system, so we ignore the error
						.catch(err => Promise.resolve("Silent Fail currently"))

				} else {
					this.showFavorites = false

					if (
						this.currentCategoriesHaveOverview &&
						this.currentCategoryIsOverview &&
						(
							this.currentCategorySiblings.some(c => c.code == params.taxon) ||
							this.currentOverviewCategory.code == params.taxon
						)
					) {
						if (this.currentScrollCategory !== params.taxon) {
							const categoryHeader = this.$refs.categoryHeader?.find(c => c.category.code == params.taxon);

							if (categoryHeader) {
								categoryHeader.scrollTo();
							}
							else if (params.taxon == this.currentOverviewCategory.code) {
								scrrroll.toTop();
							}
						}
					}
					else {
						window.scrollTo(0, 0);
						this.productPagesLoaded = 1;

						const overviewTaxon = await this.$store.dispatch('productlist/findOverviewTaxon', params.taxon);

						try {
							await this.$store.dispatch('productlist/loadProductList', {taxon: overviewTaxon?.code || params.taxon})
							if (overviewTaxon) {
								this.currentScrollCategory = overviewTaxon.code
							}
						}
						catch(err) {
							this.$store.dispatch('dialog/showDialog', {
								title: this.$t('forms.errors.error'),
								message: this.$t('forms.errors.generic'),
								confirm: 'OK',
							});
						}
					}
					// this.loading = false
				}
			}
			if (params.product) {
				this.onModalProductOpen(params.product);
			}
		},
		async onFilterApply(filters = []) {
			this.loading = true;
			for (let {filterId, active} of filters) {
				await this.$store.dispatch('productlist/setFilter', {filterId, active})
			}
			urlWatcher.change({filters: this.filtersActive.join(","), product: null})
			this.loading = false;
		},
		async onLastOrderClick(token) {
			this.loading = true;
			await this.$store.dispatch('redoOrder', { token })
			this.loading = false;
		},
		async onCartTimechanged() {
			this.loading = true
			await this.$store.dispatch('updateShoplist')
			this.loading = false
		},
		async onChildProductsAdd(product, childCodes) {
			this.loading = true;
			try {
				const item = {
						quantity: 1,
						productCode: product.code,
						variantCode: product.selectedVariant,
						childCodes: childCodes,
						isCrossSell: product.isCrossSell,
						forceCopy: true,
				};
				await this.onAddToCart(item);
				this.$refs.childproductsOverlay.close()
			}
			catch(err) {
				this.$store.dispatch('error/errorShow');
			}
			this.loading = false;
		},
		async onChildProductsEdit(item, childCodes) {
			this.loading = true;
			try {
				await this.$store.dispatch('childproducts/adjustChildProducts', { item: item, childCodes }, { root: true })
				this.$refs.childproductsOverlay.close()
			}
			catch(err) {
				this.$store.dispatch('error/errorShow');
			}
			this.loading = false;
		},
		onContentScroll() {
			if (this.productPagesLoaded < this.productPagesMax) {
				const scrollTop = window.scrollY;
				const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
				const scrollProgress = Math.min(Math.max(((scrollTop / scrollHeight) * 100), 0), 100);

				if (scrollProgress > this.productLoadThreshold) {
					this.productPagesLoaded = Math.min(this.productPagesMax, this.productPagesLoaded + 1);
				}
			}

			if (
				this.currentCategoriesHaveOverview &&
				this.currentCategoryIsOverview
			) {
				let offset = window.headerHeight ?? 0;

				if (this.isMobile()) {
					offset += window.shopCategoriesHeight;
				}

				const current = [...(this.$refs.categoryHeader || [])].reverse().find(header => window.scrollY >= header.$el.offsetTop - offset);

				if (window.scrollY == 0) {
					this.currentScrollCategory = this.currentOverviewCategory.code;
				}
				else {
					this.currentScrollCategory = current?.category?.code;
				}

				if (this.currentScrollCategory) {
					urlWatcher.change({"taxon": this.currentScrollCategory})
				}
			}
			else {
				this.currentScrollCategory = null;
			}
    },
		onShopInitialized() {
			const params = urlWatcher.readUrl()
			this.shopInitialized = true;
			this.onHashChange(params);
			this.onResize();
			this.onContentScroll();
		},
		onResize() {
			if (this.$refs.categories) {
				window.shopCategoriesHeight = this.$refs.categories.clientHeight;
				document.documentElement.style.setProperty('--shop-categories-height', `${window.shopCategoriesHeight}px`);
			}
		}
	},
	async created() {
		this.loading = true
		const urlParams = getUrlParams();
		if (this.menuMode && urlParams.menuChannel) {
			this.menuChannel = urlParams.menuChannel;
		}
		await this.$store.dispatch('initializeShoplist', { menuMode: this.menuMode, menuChannel: this.menuChannel, menuTimedAvailability: this.menuTimedAvailability })
		this.loading = false
		const params = urlWatcher.readUrl()
		this.onModalProductOpen(params.product);

		// menuMode: we force reload for the next timed menu change (either availabilty change oder next day) but minimum 1 minute
		if (this.menuTimedAvailability) {
			const msToNextChange = Math.max(60000,
				this.nextProductAvailablityChangeTime ?
					this.nextProductAvailablityChangeTime.valueOf() - dayjs().valueOf() :
					dayjs().add(24, 'hour').startOf('day').add(6, 'hour') - dayjs().valueOf()
			)

			this.isDebug() && console.log('menu-store: availability reload in %s hours', msToNextChange / 3600000)
			this.reloadTimeout = setTimeout(() => {
				location.reload()
			}, msToNextChange)
		}

		if (this.cartIsGroupOrder && this.groupOrderIsOwner) {
			this.loading = true
			await this.$store.dispatch('cart/lockGroupOrder', { locked: false })
			this.loading = false
		}
	},
	mounted() {
		urlWatcher.addListener(this.onHashChange)
		eventhub.$on(EVENT_CART_TIMECHANGED, this.onCartTimechanged)
		eventhub.$on(EVENT_SHOP_INITIALIZED, this.onShopInitialized)
		window.addEventListener('scroll', this.onContentScroll, supportsPassive ? { passive: false } : false);
		window.addEventListener('resize', this.onResize);
		if (this.shopInitialized) {
			const params = urlWatcher.readUrl()
			this.onHashChange(params);
			this.onResize();
			this.onContentScroll();
		}
	},
	destroyed() {
		urlWatcher.removeListener(this.onHashChange)
		eventhub.$off(EVENT_CART_TIMECHANGED, this.onCartTimechanged)
		eventhub.$off(EVENT_SHOP_INITIALIZED, this.onShopInitialized)
		window.removeEventListener('scroll', this.onContentScroll);
		window.removeEventListener('resize', this.onResize);
		clearTimeout(this.reloadTimeout)
	}
};
</script>

<style>
</style>
